import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Link2, Check } from "lucide-react";
import Header from "../components/Header";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";

export default function ApplyInvestor() {
  const { user, refreshMe } = useAuth();
  const { theme, themeId } = useTheme();
  const navigate = useNavigate();

  const [mode, setMode] = useState(user ? "upgrade" : "signup");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [mobile, setMobile] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const onSignup = async (e) => {
    e.preventDefault();
    setError("");
    if (!name.trim() || !email.trim() || !mobile.trim() || !password) { setError("All fields are required."); return; }
    if (password.length < 6) { setError("Password must be 6+ characters."); return; }
    setLoading(true);
    try {
      await api.post("/auth/register", {
        name: name.trim(),
        email: email.trim(),
        mobile: mobile.trim(),
        password,
        role: "investor",
        theme: themeId,
      });
      await refreshMe();
      setDone(true);
      setTimeout(() => navigate("/investor"), 1500);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const onUpgrade = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.post("/investor/apply", {});
      await refreshMe();
      setDone(true);
      setTimeout(() => navigate("/investor"), 1500);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#030303] text-white relative grain">
      <Header />
      <div className="relative min-h-screen flex items-center justify-center px-6 pt-24 pb-16">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-[400px] w-[400px] rounded-full blur-3xl opacity-20 pointer-events-none" style={{ backgroundColor: theme.color }} />
        <div className="relative w-full max-w-md p-8 bg-[#0A0A0A] border" style={{ borderColor: theme.color, boxShadow: `0 0 50px ${theme.color}33` }} data-testid="apply-investor-card">
          {done ? (
            <div className="text-center py-4 space-y-5" data-testid="investor-signup-success">
              <div className="mx-auto h-14 w-14 rounded-full flex items-center justify-center pulse-glow" style={{ backgroundColor: theme.color }}>
                <Check className="h-7 w-7 text-black" strokeWidth={3} />
              </div>
              <h3 className="font-display font-black text-xl">Investor access granted</h3>
              <p className="text-sm text-zinc-400">Routing you to your Investor dashboard…</p>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-3">
                <Link2 className="h-3 w-3 theme-text" /> Node affiliate program
              </div>
              <h1 className="font-display font-black text-3xl tracking-tight">Become an Investor</h1>
              <p className="text-sm text-zinc-400 mt-1">
                Get a unique referral code, track your network, earn for each active referral. {20} per month cap.
              </p>

              {user && user.role === "client" && (
                <div className="mt-6 grid grid-cols-2 gap-2">
                  <button onClick={() => setMode("upgrade")} className="h-10 border text-xs font-bold tracking-wide transition-colors" style={{ borderColor: mode === "upgrade" ? theme.color : "rgba(255,255,255,0.12)", color: mode === "upgrade" ? theme.color : "#a1a1aa" }}>Upgrade my account</button>
                  <button onClick={() => setMode("signup")} className="h-10 border text-xs font-bold tracking-wide transition-colors" style={{ borderColor: mode === "signup" ? theme.color : "rgba(255,255,255,0.12)", color: mode === "signup" ? theme.color : "#a1a1aa" }}>New account</button>
                </div>
              )}

              {user && user.role === "investor" ? (
                <div className="mt-6 p-4 border border-white/10 text-sm text-zinc-300">
                  You&apos;re already an Investor. <Link to="/investor" className="theme-text hover:underline">Open your dashboard</Link>.
                </div>
              ) : mode === "upgrade" && user ? (
                <form onSubmit={onUpgrade} className="mt-6 space-y-4">
                  <p className="text-sm text-zinc-400">Upgrade <span className="theme-text">{user.email}</span> to an Investor account. Your 50 Credits and waitlist status are preserved.</p>
                  {error && <p className="text-sm text-red-400" data-testid="apply-error">{error}</p>}
                  <button type="submit" disabled={loading} className="w-full h-12 font-display font-bold tracking-wide disabled:opacity-60" style={{ backgroundColor: theme.color, color: "#000", boxShadow: `0 0 28px ${theme.color}77` }} data-testid="apply-upgrade-btn">
                    {loading ? "UPGRADING…" : "UPGRADE TO INVESTOR"}
                  </button>
                </form>
              ) : (
                <form onSubmit={onSignup} className="mt-6 space-y-4" data-testid="apply-signup-form">
                  <FieldInput label="Name" value={name} onChange={setName} testid="apply-name" />
                  <FieldInput label="Email" type="email" value={email} onChange={setEmail} testid="apply-email" />
                  <FieldInput label="Mobile" value={mobile} onChange={setMobile} testid="apply-mobile" />
                  <FieldInput label="Password" type="password" value={password} onChange={setPassword} testid="apply-password" />
                  {error && <p className="text-sm text-red-400" data-testid="apply-error">{error}</p>}
                  <button type="submit" disabled={loading} className="w-full inline-flex items-center justify-center gap-2 h-12 font-display font-bold tracking-wide disabled:opacity-60" style={{ backgroundColor: theme.color, color: "#000", boxShadow: `0 0 28px ${theme.color}77` }} data-testid="apply-signup-btn">
                    {loading ? "CREATING…" : "CREATE INVESTOR ACCOUNT"}
                    <ArrowRight className="h-4 w-4" />
                  </button>
                </form>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function FieldInput({ label, value, onChange, type = "text", testid }) {
  return (
    <label className="block">
      <span className="block text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-1.5">{label}</span>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} className="w-full h-11 px-4 bg-black/60 border border-white/10 focus:border-[var(--theme-primary)] outline-none text-sm" />
    </label>
  );
}
